import { useCallback, useEffect, useRef, useState } from 'react';

export type VoiceCaptureState = 'idle' | 'recording' | 'processing' | 'denied' | 'unsupported';

export interface UseVoiceCaptureResult {
  readonly state: VoiceCaptureState;
  readonly error: string | null;
  readonly analyser: AnalyserNode | null;
  /** Begin recording. No-op if already recording. */
  readonly start: () => Promise<void>;
  /** Stop recording and resolve with the captured Blob. */
  readonly stop: () => Promise<Blob | null>;
  /** Cancel without producing a Blob. Used when the modal is dismissed. */
  readonly cancel: () => void;
}

/** Picks the best MIME type the current browser supports for MediaRecorder. */
function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',          // iOS Safari
    'audio/aac',
    'audio/ogg;codecs=opus',
  ];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c)) return c;
  }
  return undefined;
}

export function useVoiceCapture(): UseVoiceCaptureResult {
  const [state, setState] = useState<VoiceCaptureState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const stopResolveRef = useRef<((blob: Blob | null) => void) | null>(null);

  const teardown = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch { /* ignore */ }
    }
    recorderRef.current = null;
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop();
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      void audioCtxRef.current.close();
      audioCtxRef.current = null;
    }
    setAnalyser(null);
    chunksRef.current = [];
  }, []);

  useEffect(() => {
    if (typeof MediaRecorder === 'undefined' || typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setState('unsupported');
    }
    return () => teardown();
  }, [teardown]);

  const start = useCallback(async () => {
    if (state === 'recording') return;
    setError(null);
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setState('unsupported');
      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const name = (err as { name?: string }).name;
      setState('denied');
      setError(
        name === 'NotAllowedError' || name === 'PermissionDeniedError'
          ? 'Microphone permission was denied.'
          : `Could not start microphone: ${(err as Error).message}`,
      );
      return;
    }

    streamRef.current = stream;
    chunksRef.current = [];

    // Wire up Web Audio analyser for waveform visualisation (non-critical)
    try {
      const audioCtx = new AudioContext();
      const node = audioCtx.createAnalyser();
      node.fftSize = 128;
      node.smoothingTimeConstant = 0.75;
      audioCtx.createMediaStreamSource(stream).connect(node);
      audioCtxRef.current = audioCtx;
      setAnalyser(node);
    } catch { /* visualisation unavailable */ }

    const mimeType = pickMimeType();
    const recorder = mimeType
      ? new MediaRecorder(stream, { mimeType })
      : new MediaRecorder(stream);

    recorder.ondataavailable = (ev) => {
      if (ev.data.size > 0) chunksRef.current.push(ev.data);
    };
    recorder.onstop = () => {
      const type = recorder.mimeType || 'audio/webm';
      const blob = chunksRef.current.length > 0 ? new Blob(chunksRef.current, { type }) : null;
      const resolve = stopResolveRef.current;
      stopResolveRef.current = null;
      teardown();
      resolve?.(blob);
    };
    recorder.onerror = (ev) => {
      const err = (ev as unknown as { error?: Error }).error;
      setError(err ? err.message : 'recording failed');
      teardown();
      setState('idle');
    };

    recorderRef.current = recorder;
    recorder.start();
    setState('recording');
  }, [state, teardown]);

  const stop = useCallback(async () => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      return null;
    }
    setState('processing');
    return new Promise<Blob | null>((resolve) => {
      stopResolveRef.current = (blob) => {
        setState('idle');
        resolve(blob);
      };
      try { recorder.stop(); } catch {
        stopResolveRef.current = null;
        setState('idle');
        resolve(null);
      }
    });
  }, []);

  const cancel = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      stopResolveRef.current = null;
      teardown();
    }
    setState('idle');
  }, [teardown]);

  return { state, error, analyser, start, stop, cancel };
}
